import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";


import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

import { AcaoPrevCorretivaForm, AcaoPrevCorretivaSchema } from "../../../schemas/AcaoPrevCorretiva/qualidade_acoes_melhorias";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";


type Props = {
  open: boolean,
  saving: boolean,
  selectedAcoes: AcaoPrevCorretivaForm | null,
  IdAcaoQualidade: number;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onSubmit: (form: AcaoPrevCorretivaForm) => void,
  onClose: () => void,
};

export const AcoesModal = ({
  open,
  saving,
  selectedAcoes,
  IdAcaoQualidade,
  onOpenChangeHistory,
  onSubmit,
  onClose,
}: Props) => {
  const hookForm = useForm<AcaoPrevCorretivaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AcaoPrevCorretivaSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedAcoes) {
        hookForm.reset({
          Descricao: selectedAcoes.Descricao,
          IdFuncionarioResponsavel: selectedAcoes.IdFuncionarioResponsavel,
          Numero: selectedAcoes.Numero,
          DataPrevista: selectedAcoes.DataPrevista ? new Date(selectedAcoes.DataPrevista) : new Date(),
          DataRealizado: selectedAcoes.DataRealizado ? new Date(selectedAcoes.DataRealizado) : new Date(),
          IdAcaoPrevCorretiva: selectedAcoes.IdAcaoPrevCorretiva,
          IdAcaoQualidade: selectedAcoes.IdAcaoQualidade,
          Tipo: 'P',
        });
      } else {
        hookForm.reset({
            IdAcaoQualidade: IdAcaoQualidade, 
            Tipo: 'P',
            Descricao: '',
            Numero: null,
            IdFuncionarioResponsavel: null,
            DataPrevista: null,
            DataRealizado: null
        });
      }
    }
  }, [hookForm, open, selectedAcoes, IdAcaoQualidade]);

  const handleSubmit = useCallback((form: AcaoPrevCorretivaForm) => {
    if (!saving){
      onSubmit(form);
      hookForm.reset(); 
    } 
  }, [hookForm, onSubmit, saving]);

  const handleClose = useCallback(() => {
    if (!saving){
      onClose();
      hookForm.reset(); 
    } 
  }, [hookForm, onClose, saving]);

  const handleChangeHistory = useCallback(() => {
    if (selectedAcoes) {
      onOpenChangeHistory(Number(selectedAcoes.IdAcaoPrevCorretiva), LogTipoTabela.ACAO_PREV_CORRETIVA);
    }
  }, [selectedAcoes, onOpenChangeHistory]);


  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
       sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "42rem",
          },
        },
      }}
      >
      <DialogTitle>
        Ações (O que fazer para que o problema não ocorra?)
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={selectedAcoes ? "Editar" : "Cadastrar"} arrangement="fullRow">
            <>
              <div className="flex flex-row gap-4 mt-4 w-full">
                <div className="flex-1">
                  <DefaultFormInput
                    name='Descricao'
                    label="Ação"
                    multiline={true}
                    required={true}
                    rows={4}
                    reactHookForm={hookForm} 
                  />
                </div>
              </div>

              <div className="mt-4 w-full">
                <DropDownFuncionario
                  name="IdFuncionarioResponsavel"
                  readOnly={saving}
                  reactHookForm={hookForm}
                  label="Responsável Implantação"
                  required={true} 
                />
              </div>

              <div className="flex flex-row gap-4 mt-4 mb-2 w-full">
                <div className="flex-1">
                  <FormDatePickerInput
                    name='DataPrevista'
                    label='Data Prevista'
                    required={true}
                    reactHookForm={hookForm} 
                  />
                </div>

                <div className="flex-1">
                  <FormDatePickerInput
                    name='DataRealizado'
                    label='Data Realizada'
                    reactHookForm={hookForm} 
                  />
                </div>
              </div>
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="w-[220px]">
            {
              isNotNullOrUndefined(selectedAcoes) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>
          <div className="flex flex-row gap-2 w-full justify-end">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}